<script setup lang="ts">
import EmptyState from '~/components/common/EmptyState.vue'
import JobCardBody from '~/components/jobs/JobCardBody.vue'

interface Props {
  permalink: string
  company_name?: string
  preFetchedData?: any
  isLoading?: boolean
  logo_url?: string
}

const props = withDefaults(defineProps<Props>(), {
  company_name: '',
  preFetchedData: null,
  isLoading: false,
  logo_url: ''
})

const perPage = 10
const isLoading = ref(props.isLoading)
const route = useRoute()
const send_id = computed(() => (route.query.send_id as string) || '')

const processJobsWithCompanyLogo = (jobs: any[]) => {
  if (!props.logo_url || !jobs?.length) return jobs
  return jobs.map(job => ({
    ...job,
    logo: props.logo_url
  }))
}

const jobsData = ref(props.preFetchedData)

if (!props.preFetchedData && import.meta.client) {
  isLoading.value = true
  useFetch(`/api/jobs/organic/company/search`, {
    method: 'POST',
    body: {
      company_permalink: props.permalink,
      company_name: props.company_name,
      limit: perPage.toString(),
      offset: '0',
      send_id: send_id.value
    }
  }).then(({ data, pending }) => {
    jobsData.value = data.value
    isLoading.value = pending.value
  })
}

const topJobs = computed(() => {
  const jobs = jobsData.value?.jobs || []
  const seenIds = new Set<string>()
  const seenContent = new Set<string>()
  const uniqueJobs = jobs.filter((job: any) => {
    if (seenIds.has(job.j_id)) {
      return false
    }
    const locationStr = typeof job.location === 'string'
      ? job.location
      : JSON.stringify(job.location || '')
    const contentKey = `${(job.title || '').toLowerCase()}-${locationStr.toLowerCase()}`
    if (seenContent.has(contentKey)) {
      return false
    }
    seenIds.add(job.j_id)
    seenContent.add(contentKey)
    return true
  })
  return processJobsWithCompanyLogo(uniqueJobs)
})

watch(
  () => props.isLoading,
  (value) => {
    isLoading.value = value
  }
)
</script>
<template>
  <div v-if="topJobs.length > 0">
    <div class="w-full">
      <NuxtLink
        v-for="job in topJobs"
        :key="job.j_id"
        :to="`/company/${props.permalink}/viewJob/${job.j_id}`"
        class="block my-2 p-4 text-sm rounded-lg border border-gray-200 hover:border-primary-500/25 hover:border-l-2 hover:border-l-primary-500 hover:bg-primary-100/50"
      >
        <JobCardBody
          :job="job"
          title-tag="p"
          :company-display-name="props.company_name"
        />
      </NuxtLink>
    </div>
  </div>
  <div v-else class="min-h-[420px] flex items-center justify-center">
    <EmptyState message="No open positions at the moment" />
  </div>
</template>